<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatBytes } from '@/helpers/utils';
import useAuthenticatedQuery from '@/composables/apiServices/useAuthenticatedQuery';
import useSettingsService from '@/composables/apiServices/useSettingsService';
import { ROUTE_NAMES } from '@/router/conf';
import { RouterLink } from 'vue-router';
import useAuthService from '@/composables/apiServices/useAuthService';

const { me } = useAuthService();
const { data: meData } = me();
const { t } = useI18n();

const { userIndex, queriesEnabled } = useAuthenticatedQuery();
const { getUpgrade } = useSettingsService();
const { data: upgradeData } = getUpgrade();

const quota = computed(() => meData.value?.quota ?? { used: 0, allowed: 1 });

// Percentage used
const usage = computed(() => Math.ceil((quota.value.used / quota.value.allowed) * 100));

// Used
const used = computed(() => formatBytes(quota.value.used, t, 2));

// Allowed storage
const allowed = computed(() => formatBytes(quota.value.allowed, t, 2));

const upgradeTo = computed(() => {
  return (upgradeData.value?.length ?? 0) > 0 && queriesEnabled
    ? { name: ROUTE_NAMES.SETTINGS_UPGRADE, params: { userIndex: userIndex.value } }
    : null;
});
</script>

<template>
  <component
    :is="upgradeTo ? RouterLink : 'div'"
    :to="upgradeTo ?? undefined"
    class="text-label-lg text-center storage__link"
    :class="{ 'storage__link--inactive': !upgradeTo }"
    sentry-unmask
  >
    <div class="d-flex justify-space-between w-100 storage__link-content">
      <span>{{ used }}</span>
      <span v-if="upgradeTo" class="upgrade-link">{{ t('settings.upgrade.label') }}</span>
      <span>{{ allowed }}</span>
      <!-- <p>
        {{ t('sidebar.usedSpace.storageUsed') }}:
        {{
          t('sidebar.usedSpace.storagePercent', {
            usage: used,
            total: allowed
          })
        }}
      </p> -->
    </div>
    <v-progress-linear :model-value="usage" color="primary" rounded class="mt-2 mb-2" />
  </component>
</template>

<style lang="scss" scoped>
.storage__link {
  color: rgb(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  text-decoration: none;

  &-content {
    gap: 0.5rem;
  }

  &--inactive {
    // pointer-events: none;
    cursor: initial;
  }

  &:not(.storage__link--inactive):hover {
    position: relative;
    display: block;

    &::before {
      content: '';
      pointer-events: none;
      position: absolute;
      inset: -0.25rem;
      border-radius: 0.375rem;
      background-color: rgb(var(--v-theme-primary), 0.08);
    }
  }
}

.upgrade-link {
  font-size: 0.875rem;
  color: rgb(var(--v-theme-primary));
}
</style>
